import axios from "axios";
import { auth } from "../config/firebase";

export const apiClient = axios.create({
  baseURL: process.env.REACT_APP_API_BASE_URL,
});

// Request Interceptor - Add Firebase ID Token
apiClient.interceptors.request.use(
  async (config) => {
    const user = auth.currentUser;

    if (user) {
      try {
        // Get fresh Firebase ID token (handles refresh automatically)
        const idToken = await user.getIdToken(false);
        config.headers.Authorization = `Bearer ${idToken}`;
      } catch (error) {
        console.error('Error getting Firebase ID token:', error);
        // Optionally redirect to login
      }
    }

    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response Interceptor - Handle Auth Errors
apiClient.interceptors.response.use(
  (response) => response,
  async (error) => {
    const errorResponse = error.response;
    
    // Handle 401 Unauthorized - token expired or invalid
    if (errorResponse && errorResponse.status === 401) {
      const originalRequest = error.config;
      
      // Try to refresh token and retry if not already retried
      if (!originalRequest._retry) {
        originalRequest._retry = true;
        
        try {
          const user = auth.currentUser;
          if (user) {
            // Force token refresh
            const idToken = await user.getIdToken(true);
            originalRequest.headers.Authorization = `Bearer ${idToken}`;
            return apiClient(originalRequest);
          } else {
            // User not authenticated in Firebase, redirect to login
            localStorage.removeItem('isAuthenticated');
            localStorage.removeItem('phoneNumber');
            // Only redirect if not already on login page
            if (window.location.pathname !== '/login') {
              window.location.href = '/login';
            }
            return Promise.reject(new Error('Authentication failed. Please login again.'));
          }
        } catch (refreshError) {
          // Refresh failed, but check if Firebase still has user
          const user = auth.currentUser;
          if (!user) {
            // No Firebase user, redirect to login
            localStorage.removeItem('isAuthenticated');
            localStorage.removeItem('phoneNumber');
            // Only redirect if not already on login page
            if (window.location.pathname !== '/login') {
              window.location.href = '/login';
            }
          }
          // If Firebase still has user, don't redirect - let app handle it
          return Promise.reject(new Error('Token refresh failed'));
        }
      } else {
        // Already retried, check Firebase auth state before redirecting
        const user = auth.currentUser;
        if (!user) {
          // No Firebase user, redirect to login
          localStorage.removeItem('isAuthenticated');
          localStorage.removeItem('phoneNumber');
          // Only redirect if not already on login page
          if (window.location.pathname !== '/login') {
            window.location.href = '/login';
          }
        }
        // If Firebase still has user, don't redirect - let app handle it
        return Promise.reject(new Error('Authentication failed after retry'));
      }
    }
    
    // Handle other errors
    if (errorResponse && errorResponse.data) {
      if (errorResponse.data.message) {
        if (Array.isArray(errorResponse.data.message)) {
          const message = errorResponse.data.message.join("\r\n");
          return Promise.reject(new Error(message));
        } else {
          return Promise.reject(new Error(errorResponse.data.message));
        }
      } else {
        return Promise.reject(new Error("An unknown error occurred"));
      }
    } else {
      return Promise.reject(error);
    }
  }
);

const { get, post, put, delete: destroy, patch } = apiClient;
const axiosService = {
  get,
  post,
  put,
  destroy,
  patch,
};
export { axiosService };
